(() => {
  'use strict';

  const seriesKeys = ['all', 'shell', 'meister', 'signature', 'classic', 'crew', 'heritage', 'marpat', 'chess', 'curlin', 'accessory'];
  const categoryKeys = ['all', 'wallet', 'bag', 'other'];
  const sortKeys = ['default', 'price-asc', 'price-desc'];

  function createDefaultState() {
    return { series: 'all', category: 'all', sort: 'default', stock: false };
  }

  function normalizeState(state = {}) {
    return {
      series: seriesKeys.includes(state.series) ? state.series : 'all',
      category: categoryKeys.includes(state.category) ? state.category : 'all',
      sort: sortKeys.includes(state.sort) ? state.sort : 'default',
      stock: state.stock === true
    };
  }

  function normalizeProducts(products) {
    return products.map((product, ordinal) => {
      const series = Array.isArray(product.series) ? product.series : String(product.series || '').split(/\s+/);
      const price = Number(product.price);
      const index = Number(product.index);
      return {
        ordinal,
        series: [...new Set(series.filter(key => key && key !== 'all'))],
        category: categoryKeys.includes(product.category) && product.category !== 'all' ? product.category : 'other',
        available: product.available === true || product.available === 'true',
        price: Number.isFinite(price) ? price : 0,
        index: product.index !== undefined && product.index !== '' && Number.isFinite(index) ? index : ordinal
      };
    });
  }

  // Both storefront rendering and Node tests use this calculation directly.
  // Each facet excludes only its own selection, retaining the other filters.
  function deriveView(products, selected = createDefaultState()) {
    const state = normalizeState(selected);
    const rows = normalizeProducts(products);
    const matches = (row, excluded) =>
      (!state.stock || row.available) &&
      (excluded === 'series' || state.series === 'all' || row.series.includes(state.series)) &&
      (excluded === 'category' || state.category === 'all' || row.category === state.category);

    const seriesCounts = Object.fromEntries(seriesKeys.map(key => [key, 0]));
    const categoryCounts = Object.fromEntries(categoryKeys.map(key => [key, 0]));
    rows.forEach(row => {
      if (matches(row, 'series')) {
        seriesCounts.all += 1;
        row.series.forEach(key => {
          if (Object.prototype.hasOwnProperty.call(seriesCounts, key)) seriesCounts[key] += 1;
        });
      }
      if (matches(row, 'category')) {
        categoryCounts.all += 1;
        categoryCounts[row.category] += 1;
      }
    });

    const direction = state.sort === 'price-desc' ? -1 : 1;
    const ordered = rows.slice().sort((a, b) => {
      const priceOrder = state.sort === 'default' ? 0 : (a.price - b.price) * direction;
      return priceOrder || a.index - b.index || a.ordinal - b.ordinal;
    });
    const visible = ordered.filter(row => matches(row));
    return {
      state,
      total: rows.length,
      count: visible.length,
      orderedIndices: ordered.map(row => row.ordinal),
      visibleIndices: visible.map(row => row.ordinal),
      seriesCounts,
      categoryCounts
    };
  }

  // No browser global is created; CommonJS exposes only the pure core to tests.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createDefaultState, normalizeState, normalizeProducts, deriveView };
  }
  if (typeof document === 'undefined') return;

  function initialize(root) {
    if (root.dataset.cvReady === 'true') return;
    const list = root.querySelector('[data-cv-products]');
    const count = root.querySelector('[data-cv-count]');
    const empty = root.querySelector('[data-cv-empty]');
    if (!list || !count || !empty) return;
    const items = Array.from(list.querySelectorAll('[data-cv-product]'));
    const products = items.map(item => ({
      series: item.dataset.series,
      category: item.dataset.category,
      available: item.dataset.available,
      price: item.dataset.price,
      index: item.dataset.index
    }));
    const seriesButtons = Array.from(root.querySelectorAll('[data-cv-series]'));
    const categoryButtons = Array.from(root.querySelectorAll('[data-cv-category]'));
    const sort = root.querySelector('[data-cv-sort]');
    const stock = root.querySelector('[data-cv-stock]');
    let state = normalizeState({
      series: seriesButtons.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.cvSeries,
      category: categoryButtons.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.cvCategory,
      sort: sort?.value,
      stock: stock?.checked === true
    });
    let order = items.map((item, ordinal) => ordinal);
    root.dataset.cvReady = 'true';
    count.setAttribute('aria-live', 'polite');

    function updateButtons(buttons, field, counts) {
      const dataKey = field === 'series' ? 'cvSeries' : 'cvCategory';
      buttons.forEach(button => {
        const key = button.dataset[dataKey];
        button.setAttribute('aria-pressed', String(state[field] === key));
        const label = button.querySelector('[data-cv-facet-count]');
        if (label) label.textContent = String(counts[key] || 0);
      });
    }

    function render() {
      const view = deriveView(products, state);
      state = view.state;
      const visible = new Set(view.visibleIndices);
      items.forEach((item, ordinal) => { item.hidden = !visible.has(ordinal); });
      if (view.orderedIndices.some((ordinal, position) => ordinal !== order[position])) {
        const fragment = document.createDocumentFragment();
        view.orderedIndices.forEach(ordinal => fragment.appendChild(items[ordinal]));
        list.appendChild(fragment);
        order = view.orderedIndices;
      }
      updateButtons(seriesButtons, 'series', view.seriesCounts);
      updateButtons(categoryButtons, 'category', view.categoryCounts);
      if (sort) sort.value = state.sort;
      if (stock) stock.checked = state.stock;
      count.textContent = `該当 ${view.count}件 / 全 ${view.total}件`;
      empty.hidden = view.count > 0;
    }

    seriesButtons.forEach(button => button.addEventListener('click', () => {
      state = { ...state, series: button.dataset.cvSeries };
      render();
    }));
    categoryButtons.forEach(button => button.addEventListener('click', () => {
      state = { ...state, category: button.dataset.cvCategory };
      render();
    }));
    if (sort) sort.addEventListener('change', () => {
      state = { ...state, sort: sort.value };
      render();
    });
    if (stock) stock.addEventListener('change', () => {
      state = { ...state, stock: stock.checked };
      render();
    });
    root.querySelectorAll('[data-cv-reset]').forEach(button => button.addEventListener('click', () => {
      state = createDefaultState();
      render();
    }));
    render();
  }

  function scan(scope) {
    if (scope.matches?.('[data-cv-hub]')) initialize(scope);
    scope.querySelectorAll?.('[data-cv-hub]').forEach(initialize);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
